import { useContatos } from '@/api/queries/empresa';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Phone,
  Mail,
  MessageCircle,
  Linkedin,
  Instagram,
  ExternalLink,
  Copy,
  Check,
  Plus,
  Pencil,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ContatoForm } from '@/components/ContatoForm';
import { DeleteButton } from '@/components/DeleteButton';
import { instagramSearch, googleSearch, whatsappLink } from '@/lib/socialSearch';
import type { Contato } from '@/api/types';

function ensureUrl(value: string): string {
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

function cleanPhone(numero: string): string {
  return numero.replace(/\D/g, '');
}

function whatsappUrl(numero: string): string {
  const clean = cleanPhone(numero);
  const withCountry = clean.startsWith('55') ? clean : `55${clean}`;
  return `https://wa.me/${withCountry}`;
}

function formatPhone(numero: string): string {
  const d = cleanPhone(numero);
  if (d.length === 13 && d.startsWith('55')) {
    return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  }
  if (d.length === 11) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  return numero;
}

function CopyIcon({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const Icon = copied ? Check : Copy;
  async function onClick() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Não foi possível copiar');
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
      aria-label={`Copiar ${label}`}
    >
      <Icon className="h-3 w-3" />
    </button>
  );
}

export function TabContatos({
  empresaId,
  empresaCidade,
}: {
  empresaId: string;
  empresaCidade?: string;
}) {
  const { data, isLoading } = useContatos(empresaId);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contato | null>(null);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(c: Contato) {
    setEditing(c);
    setFormOpen(true);
  }

  if (isLoading) return <Skeleton className="h-48 mt-4" />;

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {data?.length ?? 0} contato{(data?.length ?? 0) === 1 ? '' : 's'}
        </h3>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Adicionar contato
        </button>
      </div>

      {!data || data.length === 0 ? (
        <p className="text-muted-foreground">Sem contatos. Clique em "Adicionar contato" pra começar.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {data.map((c) => (
            <Card key={c.id} className="p-4 space-y-3 relative group">
              <div className="absolute top-2 right-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => openEdit(c)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                  aria-label="Editar contato"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <DeleteButton entity="contato" id={c.id} iconOnly />
              </div>

              <div className="pr-8">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{c.nome}</h4>
                  {c.eh_decisor && <Badge>decisor</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">
                  {c.cargo_titulo_livre ?? c.papel}
                </p>
              </div>

              {c.telefones.length > 0 && (
                <div className="space-y-1">
                  {c.telefones.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <a
                        href={`tel:${cleanPhone(t.numero)}`}
                        className="inline-flex items-center gap-1.5 hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{formatPhone(t.numero)}</span>
                      </a>
                      <a
                        href={whatsappUrl(t.numero)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-green-600 hover:bg-green-100"
                        title="Abrir WhatsApp"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </a>
                      <CopyIcon value={formatPhone(t.numero)} label="Telefone" />
                      {t.tipo && (
                        <span className="text-xs text-muted-foreground">{t.tipo}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {c.emails.length > 0 && (
                <div className="space-y-1">
                  {c.emails.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <a
                        href={`mailto:${e.endereco}`}
                        className="inline-flex items-center gap-1.5 hover:underline truncate"
                      >
                        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate">{e.endereco}</span>
                      </a>
                      <CopyIcon value={e.endereco} label="Email" />
                    </div>
                  ))}
                </div>
              )}

              {(c.linkedin_url || c.instagram_url) && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {c.linkedin_url && (
                    <a
                      href={ensureUrl(c.linkedin_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-xs hover:bg-muted"
                    >
                      <Linkedin className="h-3 w-3" />
                      LinkedIn
                      <ExternalLink className="h-2.5 w-2.5 text-muted-foreground" />
                    </a>
                  )}
                  {c.instagram_url && (
                    <a
                      href={ensureUrl(c.instagram_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-xs hover:bg-muted"
                    >
                      <Instagram className="h-3 w-3" />
                      Instagram
                      <ExternalLink className="h-2.5 w-2.5 text-muted-foreground" />
                    </a>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-1.5 border-t pt-2">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  buscar contato:
                </span>
                <a
                  href={instagramSearch(c.nome)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-0.5 text-xs hover:bg-muted"
                  title={`Buscar "${c.nome}" no Instagram`}
                >
                  <Instagram className="h-3 w-3" />
                  Instagram
                </a>
                <a
                  href={googleSearch(c.nome, empresaCidade)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-0.5 text-xs hover:bg-muted"
                  title={`Buscar "${c.nome}"${empresaCidade ? ' em ' + empresaCidade : ''} no Google`}
                >
                  <Search className="h-3 w-3" />
                  Google
                </a>
                {(() => {
                  const tel = c.telefones[0];
                  const wa = tel ? whatsappLink(tel.numero) : null;
                  if (!wa) return null;
                  return (
                    <a
                      href={wa}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-0.5 text-xs text-green-700 hover:bg-green-50"
                      title="Abrir WhatsApp"
                    >
                      <MessageCircle className="h-3 w-3" />
                      WhatsApp
                    </a>
                  );
                })()}
              </div>

              {c.notas_livres && (
                <p className="text-xs italic text-muted-foreground border-t pt-2">
                  {c.notas_livres}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      <ContatoForm
        empresaId={empresaId}
        contato={editing}
        open={formOpen}
        onOpenChange={setFormOpen}
      />
    </div>
  );
}
